import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { createElement } from 'react';
import { FormattedNumber, defineMessages } from 'react-intl';

import { Sport } from '#src/__generated__/globalTypes.ts';
import { Horizontal } from '#src/atoms/layout/flex/index.tsx';
import { SettingsSection } from '#src/components/settings/SettingsSection/index.tsx';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useQuery } from '#src/hooks/graphql/useQuery.ts';
import { getFormattedSport, sportIcons } from '#src/lib/sports.ts';

import type {
  ActivityReportQuery,
  ActivityReportQueryVariables,
} from './__generated__/index.graphql.ts';

const messages = defineMessages({
  title: {
    defaultMessage: 'Activity Reports',
  },
  description: {
    defaultMessage:
      'See how many tournaments you have entered in the last 30 days.',
  },
});

const ACTIVITY_REPORT_QUERY = gql`
  query ActivityReportQuery {
    currentUser {
      slug
      last30DaysLineupsCount {
        football
        baseball
        nba
      }
    }
  }
` as TypedDocumentNode<ActivityReportQuery, ActivityReportQueryVariables>;

export const ActivityReports = () => {
  const { data, loading } = useQuery(ACTIVITY_REPORT_QUERY);
  const { currentUser } = useCurrentUserContext();
  if (!currentUser) return null;

  const countsBySport = {
    [Sport.FOOTBALL]: data?.currentUser?.last30DaysLineupsCount.football,
    [Sport.NBA]: data?.currentUser?.last30DaysLineupsCount.nba,
    [Sport.BASEBALL]: data?.currentUser?.last30DaysLineupsCount.baseball,
  };

  return (
    <SettingsSection {...messages}>
      <Horizontal gap={4}>
        {[Sport.FOOTBALL, Sport.NBA, Sport.BASEBALL].map(sport => (
          <Horizontal key={sport} title={getFormattedSport(sport)}>
            {createElement(sportIcons[sport])}
            {loading ? (
              '...'
            ) : (
              <FormattedNumber value={countsBySport[sport] || 0} />
            )}
          </Horizontal>
        ))}
      </Horizontal>
    </SettingsSection>
  );
};
